import { Component, OnInit, ViewChild, ElementRef, EventEmitter, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { VendorService } from '../../../services/vendor.service';
import { GlobalSearchService } from '../../../services/globalsearchservice.service';
import { PrintService } from '../../../services/print.service';
import { trigger, state, style, transition, animate } from '@angular/animations';

@Component({
	selector: 'app-vendordash-top-100',
	templateUrl: './vendordash-top-100.component.html',
	styleUrls: ['./vendordash-top-100.component.scss'],
	animations: [
		trigger('flipState', [
			state('active', style({
				transform: 'rotate3d(0, 1, 0, 179deg)',
			})),
			state('inactive', style({
				transform: 'rotateY(0)'
			})),
			transition('active => inactive', animate('500ms ease-out')),
			transition('inactive => active', animate('500ms ease-in')),
		]),
	]
})
export class VendordashTop100Component implements OnInit {
	flip = 'inactive';
	@Output() viewData = new EventEmitter<any>();
	@ViewChild('print_table') printtable: ElementRef | undefined;

	searchForm!: UntypedFormGroup;
	vendorTypes: any[] = [];
	data: any[] = [];
	viewing: any = false;
	title = 'Top 100 Vendors';
	loading = false;
	sending = false;
	sortedData: any[] = [];
	sortColumn: string = 'totalamount';
	sortDirection: 'asc' | 'desc' = 'desc';

	get cardTitle(): string {
		if (!this.searchForm) return this.title;
		const limit = this.searchForm.get('limit')?.value ?? 100;
		return 'Top ' + limit + ' Vendors';
	}

	constructor(
		private fb: UntypedFormBuilder,
		private vendorService: VendorService,
		private globalSearchService: GlobalSearchService,
		private printService: PrintService
	) {}

	ngOnInit(): void {
		const now = new Date();
		const dateTo = new Date(now);
		const dateFrom = new Date(now);
		dateFrom.setDate(dateFrom.getDate() - 365);

		this.searchForm = this.fb.group({
			datefrom: dateFrom,
			dateto: dateTo,
			limit: 100,
			supptype: '',
			inc_disabled: false,
		});

		this.vendorService.getVendorTypes().subscribe((types: any) => {
			this.vendorTypes = types || [];
		});

		this.load();
	}

	load(): void {
		if (!this.searchForm) return;
		this.loading = true;
		const v = this.searchForm.value;
		const payload = {
			datefrom: v.datefrom instanceof Date ? v.datefrom.toISOString().slice(0, 10) : v.datefrom,
			dateto: v.dateto instanceof Date ? v.dateto.toISOString().slice(0, 10) : v.dateto,
			limit: parseInt(String(v.limit), 10) || 100,
			supptype: v.supptype || undefined,
			inc_disabled: !!v.inc_disabled,
		};
		if (payload.limit > 500) payload.limit = 500;
		if (payload.limit < 1) payload.limit = 100;

		this.vendorService.getTop100Vendors(payload).subscribe((results: any) => {
			this.data = Array.isArray(results) ? results : [];
			this.sortedData = [...this.data];
			this.sortColumn = '';
			this.sortBy('totalamount');
			this.loading = false;
		}, () => { this.loading = false; });
	}

	sortBy(property: string): void {
		if (this.sortColumn === property) {
			this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
		} else {
			this.sortColumn = property;
			this.sortDirection = property === 'totalamount' || property === 'transcount' ? 'desc' : 'asc';
		}
		this.sortedData.sort((a, b) => {
			const aVal = a[property];
			const bVal = b[property];
			const num = !isNaN(parseFloat(aVal)) && !isNaN(parseFloat(bVal));
			if (num) {
				const diff = parseFloat(aVal) - parseFloat(bVal);
				return this.sortDirection === 'asc' ? diff : -diff;
			}
			const cmp = String(aVal).localeCompare(String(bVal));
			return this.sortDirection === 'asc' ? cmp : -cmp;
		});
	}

	toggleFlip(data: any, titleStr: string): void {
		if (!data) {
			this.flip = 'inactive';
		} else {
			this.flip = this.flip === 'active' ? 'inactive' : 'active';
		}
		if (this.flip === 'active') {
			this.viewing = data;
			this.title = titleStr;
			this.viewData.emit({ data, title: 'Top 100 Vendors' });
		} else {
			this.viewData.emit(false);
		}
	}

	exportPdf(): void {
		if (!this.printtable?.nativeElement) return;
		this.sending = true;
		const encoded: string = this.globalSearchService.base64encode(this.printtable.nativeElement.innerHTML);
		const limit = this.searchForm?.get('limit')?.value || 100;
		const data = {
			content: encoded,
			filename: 'top-' + limit + '-vendors.pdf',
			landscape: true,
			title: 'Top ' + limit + ' Vendors',
			subtitle: 'Created ' + new Date().toLocaleString()
		};
		this.printService.pdf(data).subscribe((result: any) => {
			this.globalSearchService.downloadPdf(result.content, data.filename);
			this.sending = false;
		});
	}

	exportXls(): void {
		if (!this.printtable?.nativeElement) return;
		this.sending = true;
		const encoded: string = this.globalSearchService.base64encode(this.printtable.nativeElement.innerHTML);
		const limit = this.searchForm?.get('limit')?.value || 100;
		const data = {
			content: encoded,
			filename: 'top-' + limit + '-vendors.xlsx',
			title: 'Top ' + limit + ' Vendors',
			subtitle: 'Created ' + new Date().toLocaleString()
		};
		this.printService.xls(data).subscribe((result: any) => {
			this.globalSearchService.downloadXls(result.content, data.filename);
			this.sending = false;
		});
	}
}
